import * as React from 'react'
import { Table } from 'react-bootstrap'

export interface IColumn<T> {
  id: string
  sort?: (a: T, b: T) => number
  label: string
  cell: (props: { data: T }) => React.ReactNode
  filterHeader?: (value: any, onChange: (value: any) => void) => React.ReactNode
  filter?: (value: any, data: T) => boolean
}

export interface IDefinition<T> {
  columns: Array<IColumn<T>>
  defaultSort?: string
  rowContainer?: (props: { data: T; children: any }) => React.ReactNode
  idGetter: (item: T) => string | number
}

export interface IDatagridProps<T> {
  definition: IDefinition<T>
  data: T[]
  children?: (data: T[]) => React.ReactNode
}

type SortType = 1 | -1

export interface IDatagridState {
  sortName: string | null
  sort: SortType
  filter: any
}

export interface IDatagridHeaderProps<T> {
  definition: IColumn<T>
  sort: SortType | null
  filter: any | null
  onSortChange: (id: string, type: SortType) => void
  onFilterChange: (id: string, value: any | null) => void
}

export interface IDatagridHeaderState {
  filterEnabled: boolean
}

export interface IDatagridRowProps<T> {
  definition: IDefinition<T>
  data: T
}

class Header<T> extends React.Component<
  IDatagridHeaderProps<T>,
  IDatagridHeaderState
> {
  constructor(props: IDatagridHeaderProps<T>) {
    super(props)

    this.state = { filterEnabled: false }

    this.onFilterClick = this.onFilterClick.bind(this)
    this.onFilterChange = this.onFilterChange.bind(this)
    this.onSortToggle = this.onSortToggle.bind(this)
  }

  onFilterClick() {
    const { definition, filter, onFilterChange } = this.props
    const { filterEnabled } = this.state

    if (definition.filterHeader == null) {
      return
    }

    if (filter != null || filterEnabled) {
      onFilterChange(definition.id, null)
      this.setState({ filterEnabled: false })

      return
    }

    this.setState({ filterEnabled: true })
  }

  onFilterChange(value: any) {
    const { definition, onFilterChange } = this.props

    onFilterChange(definition.id, value)
    this.setState({ filterEnabled: false })
  }

  onSortToggle(ev: React.SyntheticEvent<any>) {
    ev.preventDefault()

    const { sort, onSortChange, definition } = this.props

    onSortChange(definition.id, sort == null ? 1 : sort === 1 ? -1 : 1)
  }

  render() {
    const { definition, sort, filter } = this.props
    const { filterEnabled } = this.state

    const icon =
      sort == null
        ? 'sort'
        : sort === 1
          ? 'sort-by-attributes'
          : 'sort-by-attributes-alt'

    const filterShown = filter != null || filterEnabled
    const filterIcon = filterShown ? 'remove' : 'pencil'

    return (
      <th>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ flex: 1 }}>
            {filterShown &&
            definition.filterHeader != null
              ? definition.filterHeader(filter, this.onFilterChange)
              : definition.label}
          </div>
          <div style={{paddingLeft: 5}}>
            {definition.filterHeader != null && (
              <span
                className={`glyphicon glyphicon-${filterIcon}`}
                onClick={this.onFilterClick}
              />
            )}
            {definition.sort != null && (
              <span
                className={`glyphicon glyphicon-${icon}`}
                onClick={this.onSortToggle}
              />
            )}
          </div>
        </div>
      </th>
    )
  }
}

class Row<T> extends React.Component<IDatagridRowProps<T>> {
  render() {
    const { definition, data } = this.props
    const content = definition.columns.map(column => column.cell({ data }))

    if (definition.rowContainer == null) {
      return <tr>{content}</tr>
    }

    return definition.rowContainer({ data, children: content })
  }
}

export class Datagrid<T> extends React.Component<
  IDatagridProps<T>,
  IDatagridState
> {
  constructor(props: IDatagridProps<T>) {
    super(props)

    this.state = {
      sortName: props.definition.defaultSort || null,
      sort: 1,
      filter: {}
    }

    this.onFilterChange = this.onFilterChange.bind(this)
    this.onSortChange = this.onSortChange.bind(this)
  }

  onSortChange(name: string, value: SortType) {
    this.setState({
      sortName: name,
      sort: value
    })
  }

  onFilterChange(name: string, value: any) {
    this.setState({
      filter: {
        [name]: value
      }
    })
  }

  render() {
    const { children, definition, data } = this.props
    const { filter, sort, sortName } = this.state

    const filteredData = data.filter(item =>
      definition.columns
        .filter(column => column.filter != null && filter[column.id] != null)
        .every(
          column =>
            column.filter != null && column.filter(filter[column.id], item)
        )
    )

    const sortColumn =
      sortName != null
        ? definition.columns.find(column => column.id === sortName)
        : null
    const sortedData =
      sortColumn != null && sort != null && sortColumn.sort != null
        ? [...filteredData].sort(
            (a, b) =>
              sortColumn.sort != null ? sortColumn.sort(a, b) * sort : sort
          )
        : filteredData

    return (
      <Table striped={true} bordered={true} condensed={true} hover={true}>
        <thead>
          <tr>
            {definition.columns.map(column => (
              <Header
                definition={column}
                sort={column.id === sortName ? sort : null}
                filter={filter[column.id] || null}
                onSortChange={this.onSortChange}
                onFilterChange={this.onFilterChange}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map(item => (
            <Row
              key={definition.idGetter(item)}
              definition={definition}
              data={item}
            />
          ))}
          {children != null && (children as any)(sortedData)}
        </tbody>
      </Table>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/Datagrid.tsx